import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import { parseLivePath } from '~/lib/params'
import { isSourceFill } from '~/lib/sourceFill'
import { setExpandedLayerIds, setLastFillId } from '~/store/editor'
import { deleteObjects, groupObjects, setLiveObject, sortObjects, unGroupObjects } from '~/store/live'
import { dropObjectPins } from '~/store/playback'
import { cascadeDelete } from '~/store/playback/pinThunks'
import { selectLiveProps } from '~/store/playback/selectors'
import { clearObjectSelection, setObjectSelection } from '~/store/selection'

const syncObjects = createListenerMiddleware()

const deselectDeletedObjects = (getState, dispatch) => {
	const s = getState()
	const { objects } = s.live
	const objectIds = s.selection.objectIds
	const expandedIds = s.editor.expanded_layer_ids
	const selected_removed = objectIds.some(id => !objects[id])
	const expanded_removed = expandedIds.some(id => !objects[id])
	if (!selected_removed && !expanded_removed) return
	if (selected_removed) {
		dispatch(setObjectSelection(objectIds.filter(id => !!objects[id])))
	}
	if (expanded_removed) {
		dispatch(setExpandedLayerIds(expandedIds.filter(id => !!objects[id])))
	}
}

// ⚠️ Never call this for `setLiveObject`: a preset load dropping an object is what a pin defends against.
const unpinDeletedObjects = (getState, dispatch) => {
	const s = getState()
	const { objects } = s.live
	const gone = selectLiveProps(s).filter(k => {
		const p = parseLivePath(k)
		return p?.scope === 'object' && !objects[p.id]
	})
	if (gone.length) dispatch(dropObjectPins(gone))
}

// A selected source-filled shape is the one a later Sources pick refills when nothing fill-able is selected.
syncObjects.startListening({
	actionCreator: setObjectSelection,
	effect: (action, { getState, dispatch }) => {
		const s = getState()
		const id = s.selection.objectIds.findLast(oid => isSourceFill(s.live.objects[oid]?.fill))
		if (id && id !== s.editor.last_fill_id) dispatch(setLastFillId(id))
	},
})

// deselect deleted objects in case sortObjects emptied a group
syncObjects.startListening({
	actionCreator: sortObjects,
	effect: async (action, { getState, dispatch }) => {
		if (action.meta.skipHistory) return
		deselectDeletedObjects(getState, dispatch)
		unpinDeletedObjects(getState, dispatch)
	},
})

// when objects are deleted or a preset is applied, deselect all objects and filter expanded_layer_ids
syncObjects.startListening({
	matcher: isAnyOf(deleteObjects, setLiveObject),
	effect: async (action, { getState, getOriginalState, dispatch }) => {
		// A preset load is not a removal, so pins are left alone on that path.
		if (deleteObjects.match(action)) {
			const was = getOriginalState()
			const { objects } = getState().live
			const gone = Object.keys(was.playback.pins?.objects ?? {}).filter(
				id => was.live.objects[id] && !objects[id]
			)
			unpinDeletedObjects(getState, dispatch)
			if (gone.length) dispatch(cascadeDelete(gone))
		}
		if (action.meta?.skipHistory) {
			// Undo/redo: preserve valid selections
			deselectDeletedObjects(getState, dispatch)
			return
		}
		// Preset load or delete: clear all
		const s = getState()
		const { objects } = s.live
		const expanded_layer_ids = s.editor.expanded_layer_ids.filter(id => !!objects[id])
		dispatch(clearObjectSelection())
		dispatch(setExpandedLayerIds(expanded_layer_ids))
	},
})

// select the new group
syncObjects.startListening({
	actionCreator: groupObjects,
	effect: async (_, { getState, getOriginalState, dispatch }) => {
		// find new group id and select it
		const s = getState()
		const os = getOriginalState()
		const { objects } = s.live
		const prev_objects = os.live.objects

		// find new group id
		const new_group_id = Object.keys(objects).find(id => {
			return objects[id].type === 'group' && !prev_objects[id]
		})

		if (!new_group_id) return

		const expanded_layer_ids = s.editor.expanded_layer_ids.filter(id => !!objects[id])

		dispatch(setObjectSelection([new_group_id]))
		dispatch(setExpandedLayerIds(expanded_layer_ids))
	},
})

// select all the new objects from the ungrouped group
syncObjects.startListening({
	actionCreator: unGroupObjects,
	effect: async (_, { getState, getOriginalState, dispatch }) => {
		const s = getState()
		const os = getOriginalState()
		const { objects } = s.live
		const prev_objects = os.live.objects

		// find removed group id
		const removed_group_id = Object.keys(prev_objects).find(id => {
			return prev_objects[id].type === 'group' && !objects[id]
		})

		// select all the children of the removed group
		const children = [...prev_objects[removed_group_id].children]

		// filter removed group from expanded_layer_ids
		const expanded_layer_ids = s.editor.expanded_layer_ids.filter(id => !!objects[id])

		dispatch(setObjectSelection(children))
		dispatch(setExpandedLayerIds(expanded_layer_ids))
		unpinDeletedObjects(getState, dispatch)
	},
})

export default syncObjects
